import React, { useCallback, useEffect, useState } from 'react';
import { Mail, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { tabService, type WorkspaceInvitation } from '@/services/tabs';
import { toast } from 'sonner';
import { RoleBadge } from './RoleBadge';

interface IncomingInvitesBannerProps {
  refreshKey: number;
  onAccepted: () => void;
}

type IncomingInvite = WorkspaceInvitation & { workspace_name?: string };

export const IncomingInvitesBanner: React.FC<IncomingInvitesBannerProps> = ({
  refreshKey,
  onAccepted,
}) => {
  const [invites, setInvites] = useState<IncomingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await tabService.getMyWorkspaceInvitations();
      setInvites(rows);
    } catch {
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchInvites();
  }, [fetchInvites, refreshKey]);

  const handleAccept = async (invite: IncomingInvite) => {
    setAcceptingId(invite.id);
    try {
      await tabService.acceptWorkspaceInvitation(invite.id);
      toast.success(`Joined "${invite.workspace_name ?? 'shared session'}"`);
      onAccepted();
      void fetchInvites();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to accept invitation');
    } finally {
      setAcceptingId(null);
    }
  };

  if (loading || invites.length === 0) return null;

  return (
    <Card className="p-4 border border-blue-500/30 bg-blue-500/10">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-blue-500" />
        <h3 className="text-sm font-semibold text-khiimori-text-primary">
          You have {invites.length} pending invitation{invites.length === 1 ? '' : 's'}
        </h3>
      </div>
      <ul className="space-y-2">
        {invites.map((invite) => (
          <li
            key={invite.id}
            className="flex items-center gap-3 p-2 rounded-md bg-khiimori-bg-elevated/50 border border-khiimori-border/40"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-khiimori-text-primary truncate">
                {invite.workspace_name ?? 'Shared session'}
              </p>
              <p className="text-xs text-khiimori-text-muted">Invited as</p>
            </div>
            <RoleBadge role={invite.role} iconless />
            <Button
              size="sm"
              onClick={() => void handleAccept(invite)}
              disabled={acceptingId === invite.id}
              className="gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" />
              {acceptingId === invite.id ? 'Joining…' : 'Accept'}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default IncomingInvitesBanner;
